import React, { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Download, RefreshCw, LogOut, Check, X, Star, Trash2, GripVertical } from "lucide-react";
import LiveMonitor from "../components/LiveMonitor";
import BlogCalendar from "../components/BlogCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toCSV = (rows) => {
  if (!rows || !rows.length) return "";
  const keys = Array.from(rows.reduce((set, r) => { Object.keys(r).forEach((k) => set.add(k)); return set; }, new Set()));
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
};

const download = (name, rows) => {
  const csv = toCSV(rows);
  if (!csv) return;
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

const SECTIONS = [
  { key: "leads", label: "Chat Leads" },
  { key: "questions", label: "Chat Questions" },
  { key: "groups", label: "Group Bookings" },
  { key: "roommates", label: "Roommate Requests" },
  { key: "birthdays", label: "Birthday Shout-Outs" },
  { key: "newsletter", label: "Newsletter Subscribers" },
];

const Admin = () => {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [featured, setFeatured] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const [cruiseFilter, setCruiseFilter] = useState("");

  const load = async (pw, cf = cruiseFilter) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/signups`, { params: { password: pw, cruise: cf } });
      setData(res.data);
      setAuthed(true);
      const approved = (res.data.reviews || [])
        .filter((r) => r.approved)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || (b.created_at > a.created_at ? 1 : -1));
      setFeatured(approved);
      setOrderDirty(false);
    } catch (err) {
      setError("Invalid password. Please try again.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    setFeatured((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIdx(i);
    setOrderDirty(true);
  };
  const onDragEnd = () => setDragIdx(null);

  const saveOrder = async () => {
    try {
      await axios.post(`${API}/admin/reviews/order`, { ids: featured.map((r) => r.id) }, { params: { password } });
      setOrderDirty(false);
      alert("Featured order saved!");
    } catch {
      alert("Couldn't save order. Please try again.");
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!password) return;
    load(password);
  };

  const setApproval = async (id, approved) => {
    try {
      await axios.post(`${API}/admin/reviews/${id}/approve`, null, { params: { password, approved } });
      load(password);
    } catch (err) {
      alert("Action failed. Please try again.");
    }
  };

  const setConfirmed = async (id, confirmed) => {
    try {
      await axios.post(`${API}/admin/groups/${id}/confirm`, null, { params: { password, confirmed } });
      load(password);
    } catch (err) {
      alert("Action failed. Please try again.");
    }
  };

  const removeReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { params: { password } });
      load(password);
    } catch (err) {
      alert("Delete failed. Please try again.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-5">
        <form onSubmit={submit} className="w-full max-w-sm bg-[#111111] border border-white/10 p-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#E50914]/15 text-[#E50914]">
              <Lock size={20} />
            </span>
            <div>
              <h1 className="font-cond font-bold uppercase text-white text-xl tracking-wide leading-none">Admin Access</h1>
              <p className="text-white/40 text-xs mt-1">House Heads At Sea sign-ups</p>
            </div>
          </div>
          <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
          />
          {error && <p className="text-[#E50914] text-sm mt-3">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full mt-5 bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest rounded-full py-5 h-auto">
            {loading ? "Checking..." : "Enter"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="border-b border-white/10 sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <h1 className="font-cond font-bold uppercase text-lg tracking-wide">HHAS Admin <span className="text-[#E50914]">Dashboard</span></h1>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1 mr-2 border border-white/15 rounded-lg p-1">
              {[
                { k: "", label: "All Cruises" },
                { k: "escape-2026", label: "Escape '26" },
                { k: "caribbean-2027", label: "Getaway '27" },
                { k: "europe-2027", label: "Europe '27" },
              ].map((c) => (
                <button
                  key={c.k}
                  data-testid={`admin-cruise-filter-${c.k || "all"}`}
                  onClick={() => { setCruiseFilter(c.k); load(password, c.k); }}
                  className={`font-cond uppercase text-[11px] tracking-widest rounded-md px-3 py-1.5 ${cruiseFilter === c.k ? "bg-[#E50914] text-white" : "text-white/70 hover:bg-white/10"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <Button onClick={() => load(password)} variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white hover:text-black rounded-full font-cond uppercase text-xs tracking-widest">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
            <Button onClick={() => { setAuthed(false); setPassword(""); setData(null); }} variant="outline" className="border-white/20 bg-transparent text-white hover:bg-[#E50914] hover:border-[#E50914] rounded-full font-cond uppercase text-xs tracking-widest">
              <LogOut size={14} className="mr-1" /> Log out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-8 space-y-10">
        {/* Live visitor & chat monitor */}
        <LiveMonitor password={password} />

        <BlogCalendar />

        <section data-testid="promo-san2027-card" className="bg-[#111111] border border-[#D4AF37]/40 p-5 flex items-center justify-between">
          <div>
            <p className="font-cond uppercase tracking-widest text-white/60 text-xs">Promo Code SAN2027 · Evelyn Santos</p>
            <p className="text-white/40 text-xs mt-1">Confirmed group bookings using the code</p>
          </div>
          <span data-testid="promo-san2027-count" className="font-display text-4xl text-[#D4AF37]">
            {((data && data.groups) || []).filter((g) => (g.promo_code || "").toUpperCase() === "SAN2027" && g.confirmed).length}
          </span>
        </section>

        {/* Featured order — drag to reorder */}
        {featured.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-cond uppercase tracking-widest text-white text-base">
                Featured Order <span className="text-white/40 normal-case text-xs">(drag to reorder how they appear on the site)</span>
              </h2>
              <button
                onClick={saveOrder}
                disabled={!orderDirty}
                className="inline-flex items-center gap-1.5 font-cond uppercase text-xs tracking-widest px-3 py-1.5 bg-[#1B8A5A] text-white hover:bg-[#15734a] disabled:opacity-30"
              >
                <Check size={13} /> Save Order
              </button>
            </div>
            <div className="space-y-2">
              {featured.map((r, i) => (
                <div
                  key={r.id}
                  draggable
                  onDragStart={() => onDragStart(i)}
                  onDragOver={(e) => onDragOver(e, i)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-3 bg-[#111111] border p-3 cursor-grab active:cursor-grabbing ${dragIdx === i ? "border-[#F2C04C] opacity-80" : "border-white/10"}`}
                >
                  <GripVertical size={18} className="text-white/30 shrink-0" />
                  <span className="font-display text-[#E50914] text-lg w-6 text-center">{i + 1}</span>
                  {r.photo ? (
                    <img src={r.photo} alt={r.name} className="w-9 h-9 rounded-full object-cover border border-[#F2C04C] shrink-0" />
                  ) : (
                    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#E50914] text-white font-cond font-bold text-xs shrink-0">{r.initials}</span>
                  )}
                  <div className="min-w-0">
                    <p className="font-cond uppercase text-white text-sm tracking-wide leading-none">{r.name} <span className="text-white/40">· {r.trip}</span></p>
                    <p className="text-white/50 text-xs mt-1 truncate">"{r.quote}"</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Reviews panel with one-click approve */}
        {(() => {
          const reviews = (data && data.reviews) || [];
          const pending = reviews.filter((r) => !r.approved).length;
          return (
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-cond uppercase tracking-widest text-white text-base">
                  Reviews <span className="text-[#F2C04C]">({reviews.length})</span>
                  {pending > 0 && (
                    <span className="ml-2 text-[#E50914] normal-case">{pending} pending</span>
                  )}
                </h2>
                <button
                  onClick={() => download("hhas_reviews.csv", reviews)}
                  disabled={!reviews.length}
                  className="inline-flex items-center gap-1.5 text-[#F2C04C] font-cond uppercase text-xs tracking-widest hover:underline disabled:opacity-30 disabled:no-underline"
                >
                  <Download size={14} /> Export CSV
                </button>
              </div>
              {reviews.length === 0 ? (
                <p className="text-white/40 text-sm bg-[#111111] border border-white/10 p-5">No reviews yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {reviews.map((r) => (
                    <div key={r.id} className={`bg-[#111111] border p-4 ${r.approved ? "border-[#1B8A5A]/50" : "border-white/10"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {r.photo && (
                            <img src={r.photo} alt={r.name} className="w-9 h-9 rounded-full object-cover border border-[#F2C04C]" />
                          )}
                          <span className="font-cond uppercase text-white text-sm tracking-wide">{r.name}</span>
                          <span className="text-white/40 text-xs">{r.trip}</span>
                        </div>
                        <span className={`font-cond uppercase text-[10px] tracking-widest px-2 py-0.5 ${r.approved ? "bg-[#1B8A5A] text-white" : "bg-[#E50914] text-white"}`}>
                          {r.approved ? "Featured" : "Pending"}
                        </span>
                      </div>
                      <div className="flex gap-0.5 mb-2">
                        {[...Array(5)].map((_, s) => (
                          <Star key={s} size={12} className={s < (r.rating || 5) ? "text-[#F2C04C] fill-[#F2C04C]" : "text-white/20"} />
                        ))}
                      </div>
                      <p className="text-white/75 text-sm">"{r.quote}"</p>
                      <div className="flex items-center gap-2 mt-4">
                        {r.approved ? (
                          <button onClick={() => setApproval(r.id, false)} className="inline-flex items-center gap-1 text-xs font-cond uppercase tracking-widest border border-white/20 text-white/80 hover:bg-white/10 px-3 py-1.5">
                            <X size={13} /> Unfeature
                          </button>
                        ) : (
                          <button onClick={() => setApproval(r.id, true)} className="inline-flex items-center gap-1 text-xs font-cond uppercase tracking-widest bg-[#1B8A5A] text-white hover:bg-[#15734a] px-3 py-1.5">
                            <Check size={13} /> Approve & Feature
                          </button>
                        )}
                        <button onClick={() => removeReview(r.id)} className="inline-flex items-center gap-1 text-xs font-cond uppercase tracking-widest border border-white/20 text-white/60 hover:bg-[#E50914] hover:text-white hover:border-[#E50914] px-3 py-1.5">
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })()}

        {SECTIONS.map((s) => {
          const rows = (data && data[s.key]) || [];
          return (
            <section key={s.key}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-cond uppercase tracking-widest text-white text-base">
                  {s.label} <span className="text-[#F2C04C]">({rows.length})</span>
                </h2>
                <button
                  onClick={() => download(`hhas_${s.key}.csv`, rows)}
                  disabled={!rows.length}
                  className="inline-flex items-center gap-1.5 text-[#F2C04C] font-cond uppercase text-xs tracking-widest hover:underline disabled:opacity-30 disabled:no-underline"
                >
                  <Download size={14} /> Export CSV
                </button>
              </div>
              {rows.length === 0 ? (
                <p className="text-white/40 text-sm bg-[#111111] border border-white/10 p-5">No entries yet.</p>
              ) : (
                <div className="overflow-x-auto border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="bg-[#1A1A1A] text-white/60">
                      <tr>
                        {Object.keys(rows[0]).map((k) => (
                          <th key={k} className="text-left font-cond uppercase text-[11px] tracking-widest px-3 py-2 whitespace-nowrap">{k}</th>
                        ))}
                        {s.key === "groups" && <th className="px-3 py-2" />}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} className="border-t border-white/5 hover:bg-white/5">
                          {Object.keys(rows[0]).map((k) => (
                            <td key={k} className="px-3 py-2 align-top text-white/80 max-w-[280px]">
                              {typeof r[k] === "object" ? JSON.stringify(r[k]) : String(r[k] ?? "")}
                            </td>
                          ))}
                          {s.key === "groups" && (
                            <td className="px-3 py-2 align-top">
                              <button
                                data-testid={`group-confirm-btn-${r.id}`}
                                onClick={() => setConfirmed(r.id, !r.confirmed)}
                                className={`whitespace-nowrap font-cond uppercase text-[10px] tracking-widest px-3 py-1.5 ${r.confirmed ? "border border-white/20 text-white/70 hover:bg-white/10" : "bg-[#1B8A5A] text-white hover:bg-[#15734a]"}`}
                              >
                                {r.confirmed ? "Unconfirm" : "Mark Confirmed"}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default Admin;
